import { SelectOption } from "@cinesys/ui/components";

/**
 * Field definitions for the ingest metadata `NewRunModal.tsx` collects (Monday 12930235017,
 * Seth: the downstream router and stats automation need this on every run). Mirrors, field for
 * field, the customer-supplied `CineSys_Spotter_Ingest Tags` iconik view
 * (view_id `68a2f9cc-39c0-11f1-b64e-eae93db62c69`) — `name` is the exact iconik metadata key
 * (never normalised/prettified: `DATE` is upper-case, `UTC-Timecode_AI` has a hyphen, on
 * purpose), `label` is that same view's display text. Labels live here rather than
 * `strings.tsx` because they're this external schema's own vocabulary, not copy this app owns.
 *
 * Camera fields (`camera_inherited_auto_set_do_not_show_on_form` in the source JSON) are
 * intentionally absent: those inherit from the media itself at ingest and never belong on this
 * form, and there is no standalone Camera field for this customer at all (it exists in the
 * source view but belongs to a different customer).
 */

export type IngestFieldType =
  | "date"
  | "drop_down"
  | "tag_cloud"
  | "string"
  | "boolean";

export interface IngestField {
  readonly name: string;
  readonly label: string;
  readonly type: IngestFieldType;
  /** Allowed values for `drop_down`/`tag_cloud`. Always the wire `{value, label}` shape — a
   *  flat string list (most fields) is normalised to `value === label` right here, so nothing
   *  downstream has to know the source JSON's `options` came in two different shapes (the
   *  `WeekofSeason` trap: `PRE00-HOF` displays but `PRE00` is what gets submitted). Empty for
   *  `ClubUniform_AutoIngest`/`OpponentUniform_AutoIngest` — the source JSON supplies no fixed
   *  list for those, so they're free-entry tags (see `NewRunModal.tsx`'s `tag_cloud` rendering).
   */
  readonly options?: SelectOption[];
  /** Placeholder/hint text — `KickoffTC_autoingest`'s example format, from the source JSON. */
  readonly description?: string;
}

function flatOptions(values: string[]): SelectOption[] {
  return values.map((v) => ({ value: v, label: v }));
}

export const DATE_FIELD_NAME = "DATE";
export const CATEGORY_FIELD_NAME = "Category_AutoIngest";

/** Keyed in the exact order the source JSON's `Category_AutoIngest.options` lists them
 *  (Game, Practice, Intersquad, Community) — `CATEGORY_OPTIONS` below reads that order straight
 *  off these keys rather than duplicating the list, so the dropdown and the required-set lookup
 *  can never drift apart. */
export const REQUIRED_BY_CATEGORY = {
  Game: [
    "DATE",
    "Category_AutoIngest",
    "ClubUniform_AutoIngest",
    "Opponent_AI",
    "OpponentUniform_AutoIngest",
    "ShooterOrg_AI",
    "ShooterRole_AI",
    "UTC-Timecode_AI",
    "WeekofSeason",
    "KickoffTC_autoingest",
  ],
  Practice: ["DATE", "Category_AutoIngest"],
  Intersquad: [
    "DATE",
    "Category_AutoIngest",
    "ClubUniform_AutoIngest",
    "Opponent_AI",
    "OpponentUniform_AutoIngest",
  ],
  Community: ["DATE", "Category_AutoIngest"],
} as const satisfies Record<string, readonly string[]>;

export type IngestCategory = keyof typeof REQUIRED_BY_CATEGORY;

const CATEGORY_OPTIONS: SelectOption[] = flatOptions(
  Object.keys(REQUIRED_BY_CATEGORY)
);

/** The customer-facing run form (Category/DATE + everything Game/Intersquad/Practice/Community
 *  can require) — order matches the source JSON's `user_input` list. */
export const INGEST_USER_INPUT_FIELDS: IngestField[] = [
  { name: DATE_FIELD_NAME, label: "DATE", type: "date" },
  {
    name: CATEGORY_FIELD_NAME,
    label: "Category",
    type: "drop_down",
    options: CATEGORY_OPTIONS,
  },
  {
    name: "ClubUniform_AutoIngest",
    label: "Club Uniform",
    type: "tag_cloud",
    options: [],
  },
  {
    name: "Opponent_AI",
    label: "Opponent",
    type: "drop_down",
    options: flatOptions([
      "ARI",
      "ATL",
      "BAL",
      "BUF",
      "CAR",
      "CHI",
      "CIN",
      "CLE",
      "DAL",
      "DEN",
      "DET",
      "GB",
      "HOU",
      "IND",
      "JAX",
      "KC",
      "LAC",
      "LAR",
      "LV",
      "MIA",
      "MIN",
      "NE",
      "NO",
      "NYG",
      "NYJ",
      "PHI",
      "PIT",
      "SEA",
      "SF",
      "TB",
      "TEN",
      "WAS",
    ]),
  },
  {
    name: "OpponentUniform_AutoIngest",
    label: "Opponent Uniform",
    type: "tag_cloud",
    options: [],
  },
  {
    name: "ShooterOrg_AI",
    label: "Shooter Org",
    type: "drop_down",
    options: flatOptions([
      "Films",
      "Club",
      "LCC",
      "Broadcast",
      "Video Board",
      "Other",
    ]),
  },
  {
    name: "ShooterRole_AI",
    label: "Shooter Role",
    type: "drop_down",
    options: flatOptions([
      "Ground",
      "Sideline Sound",
      "Top",
      "Wire",
      "Full Game Recording",
      "General",
    ]),
  },
  {
    name: "UTC-Timecode_AI",
    label: "UTC-Timecode",
    type: "drop_down",
    options: flatOptions(["Sync'd TC", "Reel ID-Take ID", "NO SYNC"]),
  },
  {
    name: "WeekofSeason",
    label: "Week of Season",
    type: "drop_down",
    // PRE00-HOF is the only label/value mismatch in the whole schema — everything else here is
    // value === label. Getting this one wrong (submitting the label) is the single most likely
    // silent bug in this feature.
    options: [
      { label: "PRE00-HOF", value: "PRE00" },
      { label: "PRE01", value: "PRE01" },
      { label: "PRE02", value: "PRE02" },
      { label: "PRE03", value: "PRE03" },
      { label: "SB", value: "SB" },
      { label: "WK01", value: "WK01" },
      { label: "WK02", value: "WK02" },
      { label: "WK03", value: "WK03" },
      { label: "WK04", value: "WK04" },
      { label: "WK05", value: "WK05" },
      { label: "WK06", value: "WK06" },
      { label: "WK07", value: "WK07" },
      { label: "WK08", value: "WK08" },
      { label: "WK09", value: "WK09" },
      { label: "WK10", value: "WK10" },
      { label: "WK11", value: "WK11" },
      { label: "WK12", value: "WK12" },
      { label: "WK13", value: "WK13" },
      { label: "WK14", value: "WK14" },
      { label: "WK15", value: "WK15" },
      { label: "WK16", value: "WK16" },
      { label: "WK17", value: "WK17" },
      { label: "WK18", value: "WK18" },
      { label: "WK19", value: "WK19" },
      { label: "WK20", value: "WK20" },
      { label: "WK21", value: "WK21" },
    ],
  },
  {
    name: "KickoffTC_autoingest",
    label: "Kickoff TC",
    type: "string",
    description: "e.g. 13:02:45:00",
  },
];

/** Optional in every category (never gated by `REQUIRED_BY_CATEGORY`, never cleared by a
 *  Category switch) — order matches the source JSON's `functions` list. */
export const INGEST_FUNCTION_FIELDS: IngestField[] = [
  {
    name: "SkipFaceRecognition_AutoIngest",
    label: "Skip Face Recognition",
    type: "boolean",
  },
  { name: "SkipTranscription", label: "Skip Transcription", type: "boolean" },
  {
    name: "RenameFile_AutoIngest",
    label: "Rename File",
    type: "drop_down",
    options: flatOptions([
      "Game Footage",
      "Practice Footage",
      "General Footage",
      "Do Not Rename",
    ]),
  },
];

const USER_INPUT_FIELDS_BY_NAME = new Map(
  INGEST_USER_INPUT_FIELDS.map((f) => [f.name, f] as const)
);

export type IngestFieldValue = string | string[] | boolean;
export type IngestValues = Partial<Record<string, IngestFieldValue>>;

/** The user-input field names required for `category` — DATE and Category itself always among
 *  them (every entry in `REQUIRED_BY_CATEGORY` includes both). `undefined` (no category chosen
 *  yet) requires nothing. */
export function requiredFieldNames(
  category: IngestCategory | undefined
): readonly string[] {
  return category ? REQUIRED_BY_CATEGORY[category] : [];
}

/** The category-specific fields to render beyond DATE/Category, which `NewRunModal` always
 *  renders up front regardless of category — everything else only appears once it's actually
 *  required for the selected category (Practice/Community render nothing here at all). */
export function categorySpecificFields(
  category: IngestCategory | undefined
): IngestField[] {
  const required = new Set(requiredFieldNames(category));
  return INGEST_USER_INPUT_FIELDS.filter(
    (f) =>
      f.name !== DATE_FIELD_NAME &&
      f.name !== CATEGORY_FIELD_NAME &&
      required.has(f.name)
  );
}

/** `true` once `value` counts as filled for `field` — booleans (functions only, never required)
 *  always pass. */
export function isIngestFieldFilled(
  field: IngestField,
  value: IngestFieldValue | undefined
): boolean {
  if (field.type === "boolean") {
    return true;
  }
  if (field.type === "tag_cloud") {
    return Array.isArray(value) && value.length > 0;
  }
  return typeof value === "string" && value.trim().length > 0;
}

/** Gates submit: every field `REQUIRED_BY_CATEGORY[category]` names must be filled. No category
 *  selected at all is never complete. */
export function isIngestComplete(
  category: IngestCategory | undefined,
  values: IngestValues
): boolean {
  if (!category) {
    return false;
  }
  return requiredFieldNames(category).every((name) => {
    const field = USER_INPUT_FIELDS_BY_NAME.get(name);
    return !!field && isIngestFieldFilled(field, values[name]);
  });
}

/** `values` to carry over when switching TO `newCategory` — drops every user-input value whose
 *  field isn't required by the new category, so a stale Game-only value can never ride along
 *  into a Practice/Community/Intersquad submit ("don't confuse the router", Seth's own reasoning
 *  for asking for this). Function-field values (optional in every category) are untouched by a
 *  category switch. */
export function clearedForCategory(
  newCategory: IngestCategory,
  values: IngestValues
): IngestValues {
  const keep = new Set(requiredFieldNames(newCategory));
  const next: IngestValues = {};
  for (const [name, value] of Object.entries(values)) {
    if (!USER_INPUT_FIELDS_BY_NAME.has(name) || keep.has(name)) {
      next[name] = value;
    }
  }
  next[CATEGORY_FIELD_NAME] = newCategory;
  return next;
}

/** The wire payload: every field's value keyed by its iconik metadata `name`, values already
 *  resolved to the option's `value` (never its `label` — see `WeekofSeason` above) since that's
 *  the only shape ever written into `IngestValues` in the first place. Empty/unset fields are
 *  omitted rather than sent as `""`/`undefined`/`[]`. */
export function buildIngestPayload(
  values: IngestValues
): Record<string, string | string[] | boolean> {
  const payload: Record<string, string | string[] | boolean> = {};
  for (const field of [
    ...INGEST_USER_INPUT_FIELDS,
    ...INGEST_FUNCTION_FIELDS,
  ]) {
    const value = values[field.name];
    if (value === undefined) {
      continue;
    }
    if (field.type === "tag_cloud") {
      if (Array.isArray(value) && value.length > 0) {
        payload[field.name] = value;
      }
      continue;
    }
    if (field.type === "boolean") {
      payload[field.name] = !!value;
      continue;
    }
    if (typeof value === "string" && value.trim().length > 0) {
      payload[field.name] = value;
    }
  }
  return payload;
}
